import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, ArrowLeft, RefreshCcw, Lock, Eye, Database, Activity,
  CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * /admin/devices/health — Fase 3.5 read-only device health view.
 *
 * Veilig:
 *  - admin-auth verplicht (admin_token in localStorage)
 *  - geen mutatie-controls, geen live-control buttons, geen auto_safe toggles
 *  - geen secrets/PII/IP/MAC/serial/token in DOM
 *  - noindex / no-cache meta + admin nav only
 *  - data van /api/devices/intelligence/admin/{overview,recognitions,audit}
 */

function Badge({ tone = 'slate', children, testId }) {
  const tones = {
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
    slate: 'bg-white/5 text-white/70 border-white/15',
    sky: 'bg-sky-500/15 text-sky-300 border-sky-400/30',
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] uppercase tracking-wider font-semibold border ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function Card({ title, icon, action, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 p-4 sm:p-5"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
    >
      <header className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <h3 className="text-sm sm:text-base font-semibold text-white truncate" style={{ fontFamily: 'Outfit' }}>
            {title}
          </h3>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function ScalarRow({ label, value, testId }) {
  return (
    <div data-testid={testId} className="flex items-baseline justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-[12.5px] text-white/65">{label}</span>
      <span className="text-sm text-white tabular-nums">{value}</span>
    </div>
  );
}

export default function AdminDeviceHealthPage() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [overview, setOverview] = useState(null);
  const [recognitions, setRecognitions] = useState([]);
  const [audit, setAudit] = useState({ action_audit: { items: [] }, gdpr_audit: { items: [] } });
  const [plans, setPlans] = useState([]);
  const [controlHealth, setControlHealth] = useState({});
  const [pilotRec, setPilotRec] = useState(null);

  // Auth gate
  useEffect(() => {
    if (!token) {
      navigate('/admin/login?next=/admin/devices/health', { replace: true });
    }
  }, [token, navigate]);

  // noindex + no-store meta (defense-in-depth — also enforced by backend)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Device Health — Admin (intern)';
    const ensureMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"][data-admin-devhealth="1"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('data-admin-devhealth', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const ensureHE = (httpEquiv, content) => {
      let el = document.querySelector(`meta[http-equiv="${httpEquiv}"][data-admin-devhealth="1"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('http-equiv', httpEquiv);
        el.setAttribute('data-admin-devhealth', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const created = [
      ensureMeta('robots', 'noindex, nofollow, noarchive'),
      ensureMeta('googlebot', 'noindex, nofollow'),
      ensureHE('Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0'),
      ensureHE('Pragma', 'no-cache'),
    ];
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el && el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr('');
    const hdr = { Authorization: `Bearer ${token}` };
    try {
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        fetch(`${API}/devices/intelligence/admin/overview`, { headers: hdr }),
        fetch(`${API}/devices/intelligence/admin/recognitions?limit=50`, { headers: hdr }),
        fetch(`${API}/devices/intelligence/admin/audit?limit=50`, { headers: hdr }),
        fetch(`${API}/devices/intelligence/plans?limit=50`, { headers: hdr }),
        fetch(`${API}/devices/control/health`),
        fetch(`${API}/devices/control/pilot-candidates`),
      ]);
      if ([r1, r2, r3, r4].some((r) => r.status === 401)) {
        setErr('Admin-token verlopen of ongeldig. Log opnieuw in.');
        navigate('/admin/login?next=/admin/devices/health', { replace: true });
        return;
      }
      const [d1, d2, d3, d4, d5, d6] = await Promise.all([
        r1.json(), r2.json(), r3.json(), r4.json(), r5.json(), r6.json(),
      ]);
      setOverview(d1);
      setRecognitions(d2?.items || []);
      setAudit(d3 || { action_audit: { items: [] }, gdpr_audit: { items: [] } });
      setPlans(d4?.items || []);
      setControlHealth(d5 || {});
      setPilotRec(d6?.recommended_first_pilot || null);
    } catch (e) {
      setErr('Kon device health niet laden.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!token) return null;

  const safety = overview?.safety || {};
  const counts = overview?.counts || {};
  const readiness = overview?.readiness || {};
  const recentStatus = overview?.recent_status_events || [];

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(15,61,56,0.35) 0%, rgba(15,61,56,0) 55%), linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}
      data-testid="admin-device-health-page"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 sm:py-10">
        <button
          type="button"
          onClick={() => navigate('/admin?app=owner')}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-emerald-300 transition-colors mb-6"
          data-testid="admin-device-health-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar admin dashboard
        </button>

        <div className="mb-2 text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
          Intern · alleen admin/owner · read-only
        </div>
        <h1
          className="text-3xl sm:text-4xl font-semibold mb-2"
          style={{ fontFamily: 'Outfit' }}
          data-testid="admin-device-health-title"
        >
          Device Health
        </h1>
        <p className="text-sm text-white/65 leading-relaxed mb-6 max-w-prose">
          Eén veilig overzicht van de simulation-only device intelligence. Geen mutaties,
          geen live control, geen klantfacing data. Refresh om actuele tellingen op te halen.
        </p>

        {/* HARDE WAARSCHUWING — visueel rood */}
        <div
          className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 mb-5 flex items-start gap-3"
          data-testid="admin-device-health-live-control-warning"
        >
          <AlertTriangle className="w-5 h-5 text-rose-300 mt-0.5 shrink-0" />
          <div className="text-sm text-rose-100 leading-relaxed">
            <strong>Live device-control staat uit.</strong>{' '}
            <span className="text-rose-100/80">
              `GLOBAL_DEVICE_MUTATION_LOCK=true`, `AUTO_SAFE_ENABLED=false`,
              `live_adapter_registered=false`. Geen actuators worden bediend.
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <div className="flex flex-wrap items-center gap-2" data-testid="admin-device-health-badges">
            <Badge tone="emerald" testId="badge-ready-proven">READY_PROVEN · device-data selfservice</Badge>
            <Badge tone="emerald" testId="badge-ready-proven-admin">READY_PROVEN · admin visibility</Badge>
            <Badge tone="sky" testId="badge-ready-monitor">READY_BUT_MONITOR · status-event volume</Badge>
            <Badge tone="amber" testId="badge-blocked-external">BLOCKED_EXTERNAL · HomeWizard pilot keys</Badge>
            <Badge tone="rose" testId="badge-no-go">NO-GO · live control / auto_safe</Badge>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            data-testid="admin-device-health-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white/85 text-xs disabled:opacity-60"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>

        {err && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100 mb-5" data-testid="admin-device-health-err">
            {err}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {/* Safety + Blockers */}
          <Card
            title="Veiligheidsstatus"
            icon={<Lock className="w-4 h-4 text-emerald-300" />}
            testId="card-safety"
          >
            <ScalarRow label="GLOBAL_DEVICE_MUTATION_LOCK" value={String(!!safety.global_device_mutation_lock)} testId="safety-global-lock" />
            <ScalarRow label="AUTO_SAFE_ENABLED" value={String(!!safety.auto_safe_enabled)} testId="safety-auto-safe" />
            <ScalarRow label="live_adapter_registered" value={String(!!safety.live_adapter_registered)} testId="safety-live-adapter" />
            <div className="mt-3 pt-2 border-t border-white/5">
              <div className="text-[11px] uppercase tracking-wider text-white/45 mb-1">Blockers</div>
              <ul className="text-[12px] text-white/75 space-y-1" data-testid="safety-blockers">
                {(overview?.blockers || []).map((b) => (
                  <li key={b} className="flex items-start gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          {/* Counts */}
          <Card
            title="Tellingen"
            icon={<Database className="w-4 h-4 text-emerald-300" />}
            testId="card-counts"
          >
            <ScalarRow label="Herkenningen" value={counts.recognitions ?? '—'} testId="count-recognitions" />
            <ScalarRow label="Plans (simulation)" value={counts.plans ?? '—'} testId="count-plans" />
            <ScalarRow label="Suggestions (totaal)" value={counts.suggestions ?? '—'} testId="count-suggestions" />
            <ScalarRow label="Suggestions simulated" value={counts.suggestions_simulated ?? '—'} testId="count-simulated" />
            <ScalarRow label="Suggestions approved" value={counts.suggestions_approved ?? '—'} testId="count-approved" />
            <ScalarRow label="Suggestions rejected" value={counts.suggestions_rejected ?? '—'} testId="count-rejected" />
            <ScalarRow label="GDPR events" value={counts.gdpr_events ?? '—'} testId="count-gdpr" />
            <ScalarRow label="device_status_updated events" value={counts.status_events ?? '—'} testId="count-status-events" />
          </Card>

          {/* Readiness */}
          <Card
            title="Pilot readiness"
            icon={<Eye className="w-4 h-4 text-emerald-300" />}
            testId="card-readiness"
          >
            <ScalarRow label="Read-only device pilot" value={readiness.read_only_device_pilot_ready ? 'READY' : 'NOT READY'} testId="readiness-readonly" />
            <ScalarRow label="HomeWizard opt-in pilot" value={readiness.homewizard_pilot_ready ? 'READY' : 'BLOCKED_EXTERNAL'} testId="readiness-homewizard" />
            <ScalarRow label="Homey pilot" value={readiness.homey_pilot_ready ? 'READY' : 'BLOCKED_EXTERNAL'} testId="readiness-homey" />
            <ScalarRow label="Pilot opt-ins (live)" value={counts.pilot_opt_in ?? '—'} testId="count-pilot-opt-in" />
            <ScalarRow label="Pilot opt-outs (totaal)" value={counts.pilot_opt_out ?? '—'} testId="count-pilot-opt-out" />

            {(readiness.homewizard_checklist || []).length > 0 && (
              <div className="mt-3 pt-2 border-t border-white/5" data-testid="homewizard-checklist">
                <div className="text-[11px] uppercase tracking-wider text-white/45 mb-1">HomeWizard checklist</div>
                <ul className="text-[12px] text-white/75 space-y-0.5">
                  {readiness.homewizard_checklist.map((c) => (
                    <li key={c.item} className="flex items-start gap-1.5">
                      {c.ready
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" />
                        : <AlertTriangle className="w-3 h-3 text-amber-300 mt-0.5 shrink-0" />}
                      <span className={c.ready ? '' : 'text-amber-200'}>{c.item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(readiness.homey_checklist || []).length > 0 && (
              <div className="mt-3 pt-2 border-t border-white/5" data-testid="homey-checklist">
                <div className="text-[11px] uppercase tracking-wider text-white/45 mb-1">Homey checklist</div>
                <ul className="text-[12px] text-white/75 space-y-0.5">
                  {readiness.homey_checklist.map((c) => (
                    <li key={c.item} className="flex items-start gap-1.5">
                      {c.ready
                        ? <CheckCircle2 className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" />
                        : <AlertTriangle className="w-3 h-3 text-amber-300 mt-0.5 shrink-0" />}
                      <span className={c.ready ? '' : 'text-amber-200'}>{c.item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-[11.5px] text-white/55 mt-3 leading-relaxed">
              HomeWizard/Homey blijven BLOCKED_EXTERNAL tot owner-credentials zijn uitgegeven.
              Live control en auto_safe blijven uit.
            </p>
          </Card>

          {/* Fase 3.7 — Control Adapter Matrix (read-only) */}
          <Card
            title="Control adapter matrix"
            icon={<Database className="w-4 h-4 text-emerald-300" />}
            testId="card-control-matrix"
          >
            <ScalarRow label="Adapter matrix rows" value={controlHealth.adapter_matrix_count ?? '—'} testId="control-matrix-count" />
            <ScalarRow label="Simulation adapters" value={controlHealth.simulation_adapter_count ?? '—'} testId="control-sim-count" />
            <ScalarRow label="Future live stubs (disabled)" value={controlHealth.future_live_stub_count ?? '—'} testId="control-future-count" />
            <ScalarRow label="Action library verbs" value={controlHealth.action_library_count ?? '—'} testId="control-action-count" />
            <ScalarRow label="Live adapter registered" value={String(controlHealth.live_adapter_registered ?? false)} testId="control-live-registered" />
            {pilotRec && (
              <div className="mt-3 pt-2 border-t border-white/5" data-testid="control-first-pilot">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300/90 mb-1">Eerste pilotkandidaat</div>
                <p className="text-[12px] text-white/85 leading-snug">
                  <span className="font-semibold">{pilotRec.device_kind}</span>
                  {' · score '}{pilotRec.score}{'/100 · risk '}{pilotRec.risk_level}
                </p>
              </div>
            )}
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              Read-only weergave. Geen live control, geen toggles. Statusset:
              read_only · simulation_ready · pilot_ready · blocked_external · unsafe_blocked.
            </p>
          </Card>

          {/* GDPR audit summary */}
          <Card
            title="GDPR audit (laatste)"
            icon={<ShieldCheck className="w-4 h-4 text-emerald-300" />}
            testId="card-gdpr"
          >
            {(audit?.gdpr_audit?.items || []).slice(0, 8).length === 0 ? (
              <p className="text-[12.5px] text-white/55">Geen GDPR-events.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="gdpr-audit-list">
                {(audit?.gdpr_audit?.items || []).slice(0, 8).map((g, i) => (
                  <li key={(g.audit_id || '') + i} className="text-[12px] text-white/80 flex flex-wrap gap-2 items-baseline">
                    <Badge tone={g.action === 'export' ? 'sky' : 'amber'}>{g.action || 'event'}</Badge>
                    <span className="text-white/65">{g.actor || ''}</span>
                    <span className="text-white/45 ml-auto">{(g.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-white/45 mt-3">
              Geen sessie-id of e-mail zichtbaar — alleen action + actor + tijd.
            </p>
          </Card>
        </div>

        {/* Recent status events */}
        <Card
          title="Laatste device_status_updated (synthetisch)"
          icon={<Activity className="w-4 h-4 text-emerald-300" />}
          testId="card-status-events"
        >
          {recentStatus.length === 0 ? (
            <p className="text-[12.5px] text-white/55">Geen status-events ontvangen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="status-events-table">
                <thead>
                  <tr className="text-left text-white/45 uppercase text-[10.5px] tracking-wider">
                    <th className="py-2 pr-3">Tijd</th>
                    <th className="py-2 pr-3">Device kind</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2">Live?</th>
                  </tr>
                </thead>
                <tbody>
                  {recentStatus.slice(0, 10).map((e, i) => (
                    <tr key={(e.tracked_at || '') + i} className="border-t border-white/5">
                      <td className="py-2 pr-3 text-white/65 tabular-nums whitespace-nowrap">
                        {(e.tracked_at || '').slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="py-2 pr-3 text-white/85">{e.meta?.device_kind || '—'}</td>
                      <td className="py-2 pr-3 text-white/85">{e.meta?.status || '—'}</td>
                      <td className="py-2 pr-3 text-white/65">{e.meta?.source || '—'}</td>
                      <td className="py-2">
                        <Badge tone={e.meta?.live ? 'rose' : 'emerald'}>
                          {e.meta?.live ? 'LIVE' : 'safe'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Recognitions table */}
        <Card
          title="Laatste 50 recognitions"
          icon={<Eye className="w-4 h-4 text-emerald-300" />}
          testId="card-recognitions"
        >
          {recognitions.length === 0 ? (
            <p className="text-[12.5px] text-white/55">Geen recognition-rijen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="recognitions-table">
                <thead>
                  <tr className="text-left text-white/45 uppercase text-[10.5px] tracking-wider">
                    <th className="py-2 pr-3">Tijd</th>
                    <th className="py-2 pr-3">Top kind</th>
                    <th className="py-2 pr-3">Confidence</th>
                    <th className="py-2 pr-3">Tekstlengte</th>
                    <th className="py-2">Categorie</th>
                  </tr>
                </thead>
                <tbody>
                  {recognitions.slice(0, 50).map((r, i) => (
                    <tr key={(r.recognition_id || '') + i} className="border-t border-white/5">
                      <td className="py-2 pr-3 text-white/65 tabular-nums whitespace-nowrap">
                        {(r.created_at || '').slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="py-2 pr-3 text-white/85">{r.top_kind || '—'}</td>
                      <td className="py-2 pr-3 text-white/85">{(r.top_confidence ?? 0).toFixed?.(2) ?? r.top_confidence}</td>
                      <td className="py-2 pr-3 text-white/65">{r.input_text_length ?? 0}</td>
                      <td className="py-2 text-white/65">{r.declared_category || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Action audit table */}
        <Card
          title="Laatste 50 device action audit"
          icon={<Activity className="w-4 h-4 text-emerald-300" />}
          testId="card-action-audit"
        >
          {(audit?.action_audit?.items || []).length === 0 ? (
            <p className="text-[12.5px] text-white/55">Geen actie-events.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="action-audit-table">
                <thead>
                  <tr className="text-left text-white/45 uppercase text-[10.5px] tracking-wider">
                    <th className="py-2 pr-3">Tijd</th>
                    <th className="py-2 pr-3">From</th>
                    <th className="py-2 pr-3">To</th>
                    <th className="py-2">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit?.action_audit?.items || []).slice(0, 50).map((a, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-2 pr-3 text-white/65 tabular-nums whitespace-nowrap">
                        {(a.created_at || '').slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="py-2 pr-3 text-white/85">{a.from_status || a.from || '—'}</td>
                      <td className="py-2 pr-3 text-white/85">{a.to_status || a.to || a.status || '—'}</td>
                      <td className="py-2 text-white/65">{a.actor || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Optimal plans table */}
        <Card
          title="Laatste 50 optimal plans (simulation)"
          icon={<Database className="w-4 h-4 text-emerald-300" />}
          testId="card-plans"
        >
          {plans.length === 0 ? (
            <p className="text-[12.5px] text-white/55">Geen plans.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="plans-table">
                <thead>
                  <tr className="text-left text-white/45 uppercase text-[10.5px] tracking-wider">
                    <th className="py-2 pr-3">Tijd</th>
                    <th className="py-2 pr-3">Plan-id</th>
                    <th className="py-2 pr-3">Devices</th>
                    <th className="py-2 pr-3">Acties</th>
                    <th className="py-2">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.slice(0, 50).map((p, i) => (
                    <tr key={(p.plan_id || '') + i} className="border-t border-white/5">
                      <td className="py-2 pr-3 text-white/65 tabular-nums whitespace-nowrap">
                        {(p.created_at || '').slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="py-2 pr-3 text-white/85 font-mono">{(p.plan_id || '').slice(0, 14)}…</td>
                      <td className="py-2 pr-3 text-white/65">{p.summary?.total_devices ?? '—'}</td>
                      <td className="py-2 pr-3 text-white/65">{p.summary?.actions_count ?? (p.actions || []).length}</td>
                      <td className="py-2">
                        <Badge tone={p.execution_mode === 'simulation' ? 'emerald' : 'rose'}>
                          {p.execution_mode || '?'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-prose">
          Read-only intern overzicht. Geen klantfacing data. Geen mutaties via deze pagina.
          Geen secrets, IP/MAC of serienummers worden getoond.
        </p>
      </div>
    </div>
  );
}
